import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Label } from '@/components/ui/Label';
import { Spinner } from '@/components/ui/Spinner';
import { Select } from '@/components/ui/Select';
import { FileUploader } from '@/components/files/FileUploader';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizations } from '@/api/organizations';
import { useWorkspaces } from '@/api/workspaces';
import { useFolders } from '@/api/folders';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Quick Upload modal — opened from the top bar Upload button.
 * Flow:
 *   1. Pick organization (only shown for staff; client users skip straight to folder)
 *   2. Pick destination folder within that org's workspace
 *   3. Drop files / browse — FileUploader handles streaming + progress
 */
export function QuickUploadModal({ open, onClose }: Props) {
  const { user, activeOrgId } = useAuth();
  const isStaff = user?.role === 'sysadmin' || user?.role === 'manager' || user?.role === 'staff';

  // Org selection — clients have a fixed org; staff must pick.
  const orgs = useOrganizations();
  const [orgId, setOrgId] = useState<string>('');
  const clientOrgId = activeOrgId ?? user?.organization_id ?? null;
  useEffect(() => {
    if (!open) return;
    if (!isStaff && clientOrgId) setOrgId(clientOrgId);
  }, [open, isStaff, clientOrgId]);

  // Reset state on open/close
  useEffect(() => {
    if (!open) {
      setFolderId('');
      if (isStaff) setOrgId('');
    }
  }, [open, isStaff]);

  // Workspace + folder picker
  const workspaces = useWorkspaces(orgId || null);
  const workspaceId = workspaces.data?.[0]?.id;
  const folders = useFolders(workspaceId ?? null);
  const [folderId, setFolderId] = useState<string>('');

  // Flatten folder tree so the select can show nested paths.
  const folderOptions = useMemo(() => {
    type Row = { id: string; label: string };
    const data = folders.data ?? [];
    const byId = new Map(data.map((f) => [f.id, f]));
    function pathOf(id: string): string {
      const chain: string[] = [];
      let cursor: string | null = id;
      let guard = 0;
      while (cursor && guard++ < 20) {
        const f = byId.get(cursor);
        if (!f) break;
        chain.unshift(f.name);
        cursor = f.parent_id ?? null;
      }
      return chain.join(' / ');
    }
    const rows: Row[] = data
      .filter((f) => !f.is_archived)
      .map((f) => ({ id: f.id, label: pathOf(f.id) }));
    rows.sort((a, b) => a.label.localeCompare(b.label));
    return rows;
  }, [folders.data]);

  return (
    <Modal open={open} onClose={onClose} title="Upload files" size="lg">
      <div className="space-y-4">
        {isStaff && (
          <div>
            <Label htmlFor="qu-org">Organization</Label>
            <Select
              id="qu-org"
              value={orgId}
              onChange={(v) => {
                setOrgId(v);
                setFolderId('');
              }}
              options={(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
              placeholder="Select an organization..."
            />
            {orgs.isError && <p className="text-xs text-red-600 mt-1">Failed to load organizations</p>}
          </div>
        )}

        {orgId && (
          <div>
            <Label htmlFor="qu-folder">Destination folder</Label>
            {folders.isLoading || workspaces.isLoading ? (
              <div className="flex items-center gap-2 py-2 text-sm text-slate-500">
                <Spinner size={14} /> Loading folders…
              </div>
            ) : folderOptions.length === 0 ? (
              <p className="text-sm text-slate-500 py-2">
                No folders in this workspace yet. Create a folder from the Workspace page first.
              </p>
            ) : (
              <Select
                id="qu-folder"
                value={folderId}
                onChange={setFolderId}
                options={folderOptions.map((o) => ({ value: o.id, label: o.label }))}
                placeholder="Select a folder..."
              />
            )}
          </div>
        )}

        {folderId ? (
          <div>
            <Label>Files</Label>
            <FileUploader folderId={folderId} />
            <p className="text-xs text-slate-500 mt-2">
              Close this dialog when you're done — uploads continue in the background.
            </p>
          </div>
        ) : (
          <div className="text-sm text-slate-500 border-t border-slate-100 pt-4">
            {isStaff && !orgId
              ? 'Pick an organization and folder to begin uploading.'
              : 'Pick a destination folder to begin uploading.'}
          </div>
        )}
      </div>
    </Modal>
  );
}
